import sanitizeHtml from "sanitize-html";
import { getServiceSupabase } from "@/lib/supabase/service";

/** Unpublished Watchtower row used as private storage for this page. */
export const METHODOLOGY_POST_ID = "b45a1000-0000-4000-8000-000000000001";

export type MethodologyArticle = {
  kicker: string;
  title: string;
  subtitle: string;
  bodyHtml: string;
  updatedAt: string | null;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatArticleDate(iso: string | null): string {
  if (!iso) return "Sep 22, 2026";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Sep 22, 2026";
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
}

export const DEFAULT_METHODOLOGY_ARTICLE: MethodologyArticle = {
  kicker: "Methodology",
  title: "A consistent way to compare stocks.",
  subtitle: "How Bastion turns financial data into comparable scores.",
  updatedAt: null,
  bodyHtml: `
<p>Investment Bastion uses a proprietary scoring system for equity research and portfolio construction. Every company is rated by the same rules, so thousands of stocks from different countries and sectors can be compared directly.</p>
<h2>What the scoring method is</h2>
<p>It works in three steps.</p>
<ol>
<li>Key metrics are selected for the strategy. These may include valuation ratios, dividend yield, debt, growth, and profitability.</li>
<li>Each stock receives a score from 1 to 10 for every metric. A score of 10 means the stock is in the top 10% of the comparison universe for that metric. A score of 9 means the top 20%, and so on. Where a lower number is better, such as a valuation multiple or debt, the scale is reversed.</li>
<li>The metric scores are weighted and combined into a final rating. That rating shows how well the stock fits the strategy. Once it is calculated across the market, the highest scores are the clearest candidates for a closer look.</li>
</ol>
<p>Bastion currently scores more than 60 metrics, across 9 strategies, covering more than 4,000 stocks. Those scores are the foundation of the model portfolios.</p>
<p>The method gives a rules-based view of a stock, makes it possible to compare thousands of companies on the same scale, and gives a consistent way to turn a research idea into a strategy.</p>
<h2>Scoring and traditional valuation</h2>
<p>After nearly a decade at a brokerage and an asset management firm, I became frustrated with the valuation methods most of the industry still uses.</p>
<h2>Discounted cash flow</h2>
<p>A discounted cash flow model forecasts a company's future cash flows and discounts them to the present. The result is an estimate of intrinsic value, which is then compared with the share price.</p>
<p>The problem is sensitivity. A small change in the growth rate, the margin, or the discount rate can swing the conclusion from downside to upside. Changing the long-term growth rate from 2% to 3% can flip a valuation from a 10% downside to a 20% upside. That is not a stable description of the business. It leaves room to fit a target price to a story.</p>
<p>It also does not scale. Building a separate cash flow model for hundreds of companies takes a large team.</p>
<h2>Valuation multiples</h2>
<p>Comparing companies on P/E, EV/EBITDA, price to book, or dividend yield is more common, and it has its own traps.</p>
<ul>
<li><strong>A multiple on its own misleads.</strong> A low P/E and a high dividend yield often mean the business is in decline. The stock looks cheap and keeps getting cheaper.</li>
<li><strong>A screen does not say what matters most.</strong> Dozens of filters still leave every metric with the same importance. The analysis stays shallow, and it is hard to apply the same judgment twice.</li>
</ul>
<h2>Technical analysis</h2>
<p>Price-only analysis ignores the business. It can help with timing, but it is a weak foundation for long-term research. It is exposed to noise, emotion, and a story that changes with the chart.</p>
<h2>What scoring changes</h2>
<ul>
<li><strong>Less subjectivity.</strong> Scores use observable data: valuation ratios, the balance sheet, dividend yield, analyst forecasts, and market returns. Every company is judged by the same rules.</li>
<li><strong>More than one metric.</strong> A strategy combines several measures, each with its own weight. The result is a fuller view of the business than a single ratio.</li>
<li><strong>A ranked list.</strong> Once the weights are set, the system produces a ranking. There is no need to open every company by hand.</li>
<li><strong>Scale.</strong> The same logic runs across markets and sectors. A separate model is not required for every business.</li>
<li><strong>Less emotion.</strong> The process follows the rules of the strategy rather than the headline of the day.</li>
</ul>
<h2>Where it fails</h2>
<p>No system is complete. Scoring is a strong tool for analysts and portfolio managers, and it still misses things.</p>
<ul>
<li><strong>Forecasts can be wrong.</strong> In 2020, Intel scored well on Growth + Efficiency at a Reasonable Price. Nvidia's rise in AI chips and Apple's move to its own processors then took market share, and the Intel share price collapsed.</li>
<li><strong>Some risks are not in the numbers.</strong> Johnson &amp; Johnson may score well on a dividend strategy and still face large legal liabilities. Those risks often do not show up in the quantitative data.</li>
<li><strong>Sectors are not equal.</strong> Without an adjustment, banks often score well on value and efficiency, while carrying risks the standard metrics do not capture. Energy and commodity businesses often carry a crisis discount that most valuation methods also miss.</li>
</ul>
<p>Treat the score as a starting point. It is a filter for ideas, not a final decision. From there, look at the company itself: hidden risks, the sector, and anything the model cannot see. Scoring can sit next to qualitative work, the macro picture, or a traditional valuation. Its job is to narrow the field.</p>
<h2>How I use the scores</h2>
<p>A high score does not mean a stock is bought automatically. A low score does not mean it is excluded. The score is a recommendation. The decision still goes through the business, the sector, and judgment.</p>
<p>Two strategies sit at the centre of how I invest: Growth + Efficiency at a Reasonable Price (GEAR+), and Dividend Growth. I want to own high-quality businesses that are growing and operating efficiently, without overpaying for that growth.</p>
<h2>GEAR+</h2>
<p>GEAR+ looks for companies that are growing, operating efficiently, and still trading at a reasonable valuation.</p>
<ul>
<li><strong>Revenue, historical 3-year CAGR, 5%.</strong> Revenue growth over the past 3 years.</li>
<li><strong>Revenue, forward 3-year CAGR, 25%.</strong> Projected revenue growth for the next 3 years, based on analyst consensus.</li>
<li><strong>EBIT margin, 25%.</strong> Operating profit as a percentage of revenue.</li>
<li><strong>Earnings Stability Index, 10%.</strong> A custom measure of how consistent earnings per share have been over five years, from three years ago through next year's forecast.</li>
<li><strong>Forward P/E, 10%.</strong> Price relative to next year's estimated earnings.</li>
<li><strong>P/E in three years, 15%.</strong> Price relative to earnings projected three years ahead.</li>
<li><strong>1-year return, 10%.</strong> The share price return over the past 12 months.</li>
</ul>
<p>For the portfolio, only companies with a forward 3-year revenue growth score of at least 5 are considered. A higher total score means a closer fit. The strategy emphasizes fundamental strength. Valuation plays a smaller role.</p>
<p>On the stock page, GEAR+ shows the score for one company together with the charts for this strategy. The pattern it looks for is historical and forecast revenue growth, high EBIT margins, stable earnings, a moderate valuation, and a mild preference for stocks that have risen over the past year.</p>
<p>The Earnings Stability Index uses earnings per share over five years, from three years of history through next year's forecast. A company receives 0.5 points for each profitable year, and another 1.5 points for each year in which earnings per share were the highest of the prior four years. The maximum is 10, for stable earnings that keep making new highs.</p>
<p>Price momentum has a small weight because, all else equal, stocks with positive momentum have tended to keep doing well over the short and medium term. A sharp decline over the past year often reflects a problem the market is already pricing.</p>
<p>Banks and other financial companies are a special case. Revenue growth and EBIT margin are poor measures for that business model. GEAR+ uses earnings growth and return on equity instead.</p>
<p>I review the watchlist, notes, and updated scores every quarter, then rebalance. A stock's weight depends on the updated score and on conviction.</p>
<h2>Dividend Growth</h2>
<p>This strategy looks for companies that pay a dividend and keep growing. Value comes first. Growth and the safety of the dividend still matter. I am not looking for a high yield that is a value trap: a stock that looks cheap and keeps lagging because the business is impaired. The aim is income, quality, and a future, together.</p>
<ul>
<li><strong>Dividend yield, 25%.</strong> Current income relative to the price.</li>
<li><strong>Free cash flow yield, 20%.</strong> Free cash flow relative to the market value.</li>
<li><strong>Expected 3-year dividend growth, 10%.</strong> Analyst consensus on forward dividend growth.</li>
<li><strong>Revenue, forward 3-year CAGR, 20%.</strong> Projected revenue growth over the next 3 years.</li>
<li><strong>Payout as a share of free cash flow, 5%.</strong> A lower payout is a safer dividend.</li>
<li><strong>Net debt to EBITDA, 5%.</strong> Balance sheet strength.</li>
<li><strong>5-year total return, 15%.</strong> Long-term value created for shareholders.</li>
</ul>
<p>Two filters apply. The dividend yield score must be at least 2, so very low yields drop out. The forward 3-year revenue growth score must be at least 3, so the business still needs a moderate growth outlook.</p>
<p>Scores on this strategy tend to be lower than on the others. Stocks that combine a real yield with real growth are rare. A total score of 50 or above is already a strong result. The goal is balance, not the highest yield.</p>
<p>The 5-year return is there to catch value traps. A high yield with years of weak returns is a warning. A strong long-term return means the company paid the dividend and the share price also compounded.</p>
<p>As with GEAR+, I review the watchlist, notes, and charts every three months before rebalancing.</p>
<h2>Two smaller strategies</h2>
<p>Alongside the core strategies, I keep a smaller allocation for tactical and experimental portfolios. They are a place to test ideas without letting them take over the main book.</p>
<h2>Smart Momentum</h2>
<p>This is a small speculative portfolio. The point is to give emotion a bounded place. The risk is falling for a headline and putting half the portfolio into one story. I have seen professional managers do that in their personal accounts. The speculative book is where those impulses go, including trades that can change from week to week.</p>
<p>Ideas come from the news, from valuation screens, from unusual activity, and from Smart Momentum itself.</p>
<p>Smart Momentum starts with recent price strength and rising volume, then keeps only the stocks that are still inexpensive on earnings three years out.</p>
<ul>
<li><strong>1-month return, 17%.</strong> Recent momentum.</li>
<li><strong>3-month return, 17%.</strong> A trend that has lasted a quarter.</li>
<li><strong>1-year return, 17%.</strong> Longer momentum.</li>
<li><strong>Inverted beta rank, 17%.</strong> Higher beta scores higher.</li>
<li><strong>Inverted standard deviation rank, 17%.</strong> Higher historical price fluctuation scores higher.</li>
<li><strong>Volume change, 90 days, 17%.</strong> A rise in trading volume over the past three months.</li>
<li><strong>P/E in three years, filter only.</strong> Not in the score. A stock must score at least 4, so it is not expensive on earnings three years ahead.</li>
</ul>
<p>There is no fixed rebalance date. Some weeks produce a new idea. Some months produce none.</p>
<h2>Buy the Fortress Dip</h2>
<p>The question, from 2020, was whether to buy stocks that have lagged the market but still show long-term growth and efficiency. These are strong businesses that are temporarily out of favour.</p>
<ul>
<li><strong>1-year return, filter only.</strong> Only stocks with a score of 3 or lower, the bottom 30% of the market over the past year.</li>
<li><strong>Revenue, forward 3-year CAGR, 20%.</strong></li>
<li><strong>EPS growth two years ahead, 10%.</strong></li>
<li><strong>EPS growth three years ahead, 10%.</strong></li>
<li><strong>EBITDA margin forecast, three years ahead, 20%.</strong></li>
<li><strong>Net income margin forecast, three years ahead, 20%.</strong></li>
<li><strong>P/E in three years, 20%.</strong> The rebound also has to be reasonably priced.</li>
</ul>
<p>Once a year I select 10 of these stocks and rebalance. It is an experiment, and I track it so the result can change the method.</p>
<h2>Additional scoring models</h2>
<p>Single-factor and hybrid models sit next to the core strategies. They show what the market is rewarding right now: which companies are growing fastest, where momentum is fading, and whether margins are expanding or compressing. They also suggest changes to the core models.</p>
<p>Forecasts in the growth model use analyst consensus.</p>
<h2>Growth</h2>
<ul>
<li><strong>Revenue, historical 3-year CAGR, 14%.</strong></li>
<li><strong>Revenue growth, next year, 14%.</strong></li>
<li><strong>Revenue, forward 3-year CAGR, 14%.</strong></li>
<li><strong>EPS, historical 3-year CAGR, 14%.</strong></li>
<li><strong>EPS growth, next year, 14%.</strong></li>
<li><strong>EPS growth, two years ahead, 14%.</strong></li>
<li><strong>EPS growth, three years ahead, 14%.</strong></li>
</ul>
<h2>Quality</h2>
<ul>
<li><strong>EBIT margin, 14%.</strong> Core operating profitability.</li>
<li><strong>Net income margin, 14%.</strong></li>
<li><strong>Free cash flow margin, 14%.</strong></li>
<li><strong>Return on equity, 14%.</strong></li>
<li><strong>Return on assets, 14%.</strong></li>
<li><strong>Earnings Stability Index, 14%.</strong></li>
<li><strong>Net debt to EBITDA, 14%.</strong> Lower leverage scores higher.</li>
</ul>
<h2>Deep Value</h2>
<ul>
<li><strong>Forward P/E, 14%.</strong></li>
<li><strong>P/E in three years, 14%.</strong></li>
<li><strong>Forward EV/EBITDA, 14%.</strong></li>
<li><strong>Forward EV/EBIT, 14%.</strong></li>
<li><strong>Free cash flow yield, 14%.</strong></li>
<li><strong>Dividend yield, 14%.</strong></li>
<li><strong>Price to book, 14%.</strong></li>
</ul>
<h2>Price Momentum</h2>
<p>Winners have tended to keep winning. Year-to-date return is recorded and left out of the score.</p>
<ul>
<li><strong>1-week return, 17%.</strong></li>
<li><strong>1-month return, 17%.</strong></li>
<li><strong>3-month return, 17%.</strong></li>
<li><strong>1-year return, 17%.</strong></li>
<li><strong>3-year return, 17%.</strong></li>
<li><strong>5-year return, 17%.</strong></li>
</ul>
<h2>Deep Value + Momentum</h2>
<p>Inexpensive stocks that are already starting to rise.</p>
<ul>
<li><strong>Forward P/E, 14%.</strong></li>
<li><strong>P/E in three years, 14%.</strong></li>
<li><strong>Dividend yield, 14%.</strong></li>
<li><strong>1-month return, 14%.</strong></li>
<li><strong>3-month return, 14%.</strong></li>
<li><strong>1-year return, 14%.</strong></li>
<li><strong>Volume change, 90 days, 14%.</strong></li>
</ul>
<h2>Efficiency + Growth</h2>
<p>Companies that are growing and converting that growth into profits.</p>
<ul>
<li><strong>Revenue, historical 3-year CAGR, 14%.</strong></li>
<li><strong>Revenue, forward 3-year CAGR, 14%.</strong></li>
<li><strong>EPS, forward 3-year CAGR, 14%.</strong></li>
<li><strong>EBIT margin, 14%.</strong></li>
<li><strong>Net income margin, 14%.</strong></li>
<li><strong>Return on assets, 14%.</strong></li>
<li><strong>Earnings Stability Index, 14%.</strong></li>
</ul>
<h2>A starting point</h2>
<p>Investing is a decision under uncertainty. Cash flow models and single multiples are often too sensitive, too thin, or impossible to apply across a whole market.</p>
<p>Scoring gives one set of rules for growth, efficiency, value, or resilience. It does not replace a close look at the company. It is the disciplined first pass: surface the idea, test it, and build the portfolio from a method rather than from a headline.</p>
<p>The longer version of this note is on the <a href="https://investmentbastion.substack.com/p/bastion-stock-valuation-strategies">Investment Bastion newsletter</a>.</p>
`.trim(),
};

/** Kept inside the article HTML so the diagram can sit between any two blocks. */
export const METHOD_DIAGRAM_MARKER = '<p data-method-diagram="1">&#8203;</p>';

const DIAGRAM_MARKER_RE = /<p\b[^>]*\bdata-method-diagram=["']1["'][^>]*>[\s\S]*?<\/p>/i;

export function splitAroundDiagram(html: string): [string, string] {
  const match = html.match(DIAGRAM_MARKER_RE);
  if (match && match.index !== undefined) {
    return [html.slice(0, match.index), html.slice(match.index + match[0].length).replace(DIAGRAM_MARKER_RE, "")];
  }
  const end = html.indexOf("</ol>");
  if (end === -1) return [html, ""];
  const cut = end + "</ol>".length;
  return [html.slice(0, cut), html.slice(cut)];
}

export type ArticleBlockKind = "strategies" | "multi-strategies" | "comparison" | "example";

export const ARTICLE_BLOCK_MARKERS: Record<ArticleBlockKind, string> = {
  strategies: '<p data-method-block="strategies">&#8203;</p>',
  "multi-strategies": '<p data-method-block="multi-strategies">&#8203;</p>',
  comparison: '<p data-method-block="comparison">&#8203;</p>',
  example: '<p data-method-block="example">&#8203;</p>',
};

const ARTICLE_BLOCK_RE = /<p\b[^>]*\bdata-method-block=["'](strategies|multi-strategies|comparison|example)["'][^>]*>[\s\S]*?<\/p>/gi;

export type ArticleSegment =
  | { type: "html"; html: string }
  | { type: "block"; kind: ArticleBlockKind };

/** Splits the article on embedded block markers; each kind is rendered once, at its first marker. */
export function splitArticleBlocks(html: string): ArticleSegment[] {
  const segments: ArticleSegment[] = [];
  const seen = new Set<ArticleBlockKind>();
  let last = 0;
  for (const match of html.matchAll(ARTICLE_BLOCK_RE)) {
    const index = match.index ?? 0;
    if (index > last) segments.push({ type: "html", html: html.slice(last, index) });
    last = index + match[0].length;
    const kind = match[1].toLowerCase() as ArticleBlockKind;
    if (seen.has(kind)) continue;
    seen.add(kind);
    segments.push({ type: "block", kind });
  }
  if (last < html.length) segments.push({ type: "html", html: html.slice(last) });
  return segments;
}

export function joinAroundDiagram(before: string, after: string): string {
  const strip = (value: string) => value.replace(DIAGRAM_MARKER_RE, "");
  return `${strip(before)}${METHOD_DIAGRAM_MARKER}${strip(after)}`;
}

export function sanitizeArticleHtml(input: string): string {
  return sanitizeHtml(input, {
    allowedTags: ["p", "h2", "ul", "ol", "li", "strong", "em", "a", "br"],
    allowedAttributes: {
      a: ["href", "target", "rel"],
      p: ["data-method-diagram", "data-method-block"],
    },
    allowedSchemes: ["http", "https", "mailto"],
    transformTags: {
      b: "strong",
      i: "em",
      div: "p",
      h1: "h2",
      h3: "h2",
      h4: "h2",
      a: (_tagName, attribs) => {
        const href = attribs.href || "";
        const internal = href.startsWith("/") && !href.startsWith("//");
        return {
          tagName: "a",
          attribs: internal
            ? { href }
            : { href, target: "_blank", rel: "noopener noreferrer" },
        };
      },
    },
  }).trim();
}

function plainText(value: unknown, max: number): string {
  return String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}

const WRAPPED_MARKER_RE = /<(h2|li)>\s*(<p\b[^>]*\bdata-method-(?:block|diagram)=[^>]*>[\s\S]*?<\/p>)\s*<\/\1>/gi;

/** contentEditable can drop an embed marker inside a heading; lift it back out so it stays a standalone block. */
function unwrapBlockMarkers(html: string): string {
  return html.replace(WRAPPED_MARKER_RE, "$2");
}

export function normalizeMethodologyArticle(input: {
  kicker?: unknown;
  title?: unknown;
  subtitle?: unknown;
  bodyHtml?: unknown;
  updatedAt?: string | null;
}): MethodologyArticle {
  const title = plainText(input.title, 180);
  const bodyHtml = unwrapBlockMarkers(sanitizeArticleHtml(String(input.bodyHtml ?? "")));
  return {
    kicker: plainText(input.kicker, 40) || "Methodology",
    title,
    subtitle: plainText(input.subtitle, 240),
    bodyHtml,
    updatedAt: input.updatedAt ?? null,
  };
}

type StoredMethodology = {
  kicker?: string;
  subtitle?: string;
  bodyHtml?: string;
};

export async function getMethodologyArticle(): Promise<MethodologyArticle> {
  try {
    const supabase = getServiceSupabase();
    const { data, error } = await supabase
      .from("watchtower_posts")
      .select("title, content, updated_at, is_published")
      .eq("id", METHODOLOGY_POST_ID)
      .maybeSingle();

    if (error || !data?.content) return DEFAULT_METHODOLOGY_ARTICLE;

    let stored: StoredMethodology = {};
    try {
      stored = JSON.parse(data.content) as StoredMethodology;
    } catch {
      return DEFAULT_METHODOLOGY_ARTICLE;
    }

    const article = normalizeMethodologyArticle({
      kicker: stored.kicker,
      title: data.title,
      subtitle: stored.subtitle,
      bodyHtml: stored.bodyHtml,
      updatedAt: data.updated_at,
    });

    if (!article.title || !article.bodyHtml) return DEFAULT_METHODOLOGY_ARTICLE;
    return article;
  } catch (error) {
    console.error("[methodology] Failed to load article:", error);
    return DEFAULT_METHODOLOGY_ARTICLE;
  }
}

export async function saveMethodologyArticle(
  input: {
    kicker?: unknown;
    title?: unknown;
    subtitle?: unknown;
    bodyHtml?: unknown;
  },
  authorId: string,
): Promise<MethodologyArticle> {
  const article = normalizeMethodologyArticle(input);
  if (!article.title) {
    throw new Error("Add a title before saving.");
  }
  if (!article.bodyHtml) {
    throw new Error("Add some text before saving.");
  }

  const supabase = getServiceSupabase();
  const content = JSON.stringify({
    kicker: article.kicker,
    subtitle: article.subtitle,
    bodyHtml: article.bodyHtml,
  });

  const { data: existing, error: existingError } = await supabase
    .from("watchtower_posts")
    .select("id")
    .eq("id", METHODOLOGY_POST_ID)
    .maybeSingle();

  if (existingError) {
    throw new Error(existingError.message);
  }

  const row = {
    title: article.title,
    content,
    excerpt: article.subtitle,
    tags: ["__site_article"],
    is_published: false,
    is_featured: false,
  };

  const query = existing
    ? supabase.from("watchtower_posts").update(row).eq("id", METHODOLOGY_POST_ID)
    : supabase.from("watchtower_posts").insert({
        ...row,
        id: METHODOLOGY_POST_ID,
        author_id: authorId,
      });

  const { data, error } = await query.select("updated_at").single();
  if (error) throw new Error(error.message);

  return {
    ...article,
    updatedAt: data.updated_at,
  };
}
